<template lang="pug">
.form-field-label
  .form-field-label-text(v-if="label") {{ label }}
  q-input.date-input-class(:value="value", @click.stop="openDateModal()", readonly, :class="getInputClass")
    template(v-slot:prepend)
      inline-svg.datetime-icon(:src="require(`../../../../assets/icons/calendar.svg`)", @click.stop="openDateModal()")
    template(v-slot:append)
      q-icon.cancel-datetime-field(name="cancel", v-if="showCancelIcon", @click.capture="resetField()")
    hint-component(v-if="hint", :text="hint")
  q-dialog#q-datetime-modal(v-model="openModal")
    q-card.row.radius(flat)
      q-date(
        v-model="currentDate",
        :mask="mask",
        :locale="currentDatesLocale",
        :default-view="'Calendar'",
        today-btn,
        :options="optionsFn"
      )
        .row.justify-end
          q-btn.dialog-controls(
            flat,
            @click="openModal = false",
            :label="buttonCancelLabel",
            v-if="type !== 'datetime'"
          )
          q-btn.dialog-controls(flat, @click="checkDate", :label="buttonCloseLabel", v-if="type !== 'datetime'")
      q-time(v-model="currentDate", :mask="mask", format24h, v-if="type === 'datetime'", now-btn)
        .row.justify-end
          q-btn.dialog-controls(
            flat,
            @click="openModal = false",
            :label="buttonCancelLabel",
            v-if="type === 'datetime'"
          )
          q-btn.dialog-controls(flat, @click="checkDate", :label="buttonCloseLabel", v-if="type === 'datetime'")
</template>

<script setup>
import { Datetime } from "vue-datetime";
import { format, parse } from "date-fns";
import { formatDateAsISO } from "@/services/formatDateAsISO";
import { computed, ref, watch } from "vue";
import { currentLocale } from "@/services/useLocales";
import i18n from "@/plugins/vue-i18n/index";
import hintComponent from "../../helpers/hint.vue";

const props = defineProps({
  /** Параметр отвечающий за тип поля date или datetime. Пример: "date" */
  type: { type: String, default: () => "date" },
  /** Параметр значения текущего поля. Пример: "01.01.1970" */
  value: { type: String, default: () => "" },
  /** Параметр назначающий минимальную дату для поля. Пример: "01.01.1970" */
  minDate: { type: String, default: () => "" },
  /** Параметр назначающий максимальную дату для поля. Пример: "01.01.1970" */
  maxDate: { type: String, default: () => "" },
  /** Параметр включающий в себя event из формы расписаний. Используется только там. Пример: 
  {
    "name": "",
    "description": "",
    "periodType": {},
    "period": [],
    "startedAt": "",
    "finishedAt": "",
    "works": {
      "options": [],
      "items": [],
      "toRemove": [],
      "hasNextPage": false,
      "page": 1
    },
    "materials": [],
    "materialsToRemove": [],
    "materialsOptions": [],
    "deadline": "",
    "id": null
  }
   */
  event: { type: Object, default: () => {} },
  /** Параметр отвечающий за отключение поля. Пример: true */
  disable: { type: Boolean, default: () => false },
  /** Проп для вывода подсказки */
  hint: { type: String || null, default: () => null },
  itsFormField: { type: Boolean, default: () => false },
  label: { type: String || null, default: () => null },
});

const emit = defineEmits([
  /** Эмит для назначения нового value при выборе новой даты. Пример: "01.01.1970" */
  "select-date",
  "change",
]);

const value = ref(props.value);
const openModal = ref(false);
const currentDate = ref("");

const minDate = computed(() => props.minDate);
const maxDate = computed(() => props.maxDate);

const mask = computed(() => "DD.MM.YYYY");
const type = computed(() => props.type);
const buttonCancelLabel = computed(() => i18n["messages"][currentLocale.value]["buttonCancelLabel"]);
const buttonCloseLabel = computed(() => i18n["messages"][currentLocale.value]["buttonCloseLabel"]);
const currentDatesLocale = computed(() => i18n["messages"][currentLocale.value]["date"]);
const inputAdditionalClass = computed(() => (props.disable ? "disable" : ""));
const showCancelIcon = computed(() => value.value && !props.disable);

const getInputClass = computed(() => {
  if (props.itsFormField) {
    return "form-field form-date " + inputAdditionalClass.value;
  }
  return inputAdditionalClass.value;
});

const getCurrentDate = () => {
  const date = new Date();
  return format(date, "dd.MM.yyyy");
};

const openDateModal = () => {
  if (props.disable) {
    return;
  }
  let newDateVal = getCurrentDate();

  if (value.value != "") {
    newDateVal = value.value;
  }

  currentDate.value = newDateVal;
  openModal.value = true;
};

const checkDate = () => {
  value.value = currentDate.value;
  openModal.value = false;
  emitValue(value.value);
};

const emitValue = val => {
  if (props.event) {
    emit("select-date", { val: val, event: props.event });
    return;
  }
  emit("select-date", val);
  emit("change", val);
};

const resetField = () => {
  value.value = "";
  emitValue(value.value);
};

const optionsFn = date => {
  const parsedMinDate = parse(minDate.value, "dd.MM.yyyy", new Date());
  const parsedMaxDate = parse(maxDate.value, "dd.MM.yyyy", new Date());

  if (minDate.value && maxDate.value) {
    return date >= format(parsedMinDate, "yyyy/MM/dd") && date <= format(parsedMaxDate, "yyyy/MM/dd");
  }

  if (minDate.value) {
    return date >= format(parsedMinDate, "yyyy/MM/dd");
  } else if (maxDate.value) {
    return date <= format(parsedMaxDate, "yyyy/MM/dd");
  } else {
    return date > "";
  }
};

watch(minDate, async (newVal, oldVal) => {
  if (newVal === "") {
    // Сброс поля нужен, т.к. изменилось его минимальное значение на пустую строку
    value.value = "";
    emitValue(value.value);
    return;
  }
  if (newVal > value.value) {
    value.value = newVal;
    emitValue(value.value);
  }
});

watch(maxDate, async (newVal, oldVal) => {
  if (newVal === "") {
    // Сброс поля нужен, т.к. изменилось его максимальное значение на пустую строку
    value.value = "";
    emitValue(value.value);
    return;
  }
  if (newVal < value.value) {
    value.value = newVal;
    emitValue(value.value);
  }
});
</script>

<style lang="scss">
@import "../../../../assets/styles/forms/fields/date";

.date-input-class {
  border: none !important;
  cursor: pointer;
  .q-field__prepend {
    padding-left: 15px;
    height: 41px;
    path {
      fill: var(--datetime-icon-fill);
    }
  }
  .q-field__append {
    height: 41px;
    i {
      margin-top: 0;
      margin-right: -1px;
      font-size: 14px;
    }
  }
  .q-field__control,
  .q-field__control:before,
  .q-field__control:hover:before {
    color: transparent;
    border: none;
  }
  input {
    font-size: 16px;
    margin-top: 0 !important;
  }

  &.disable {
    cursor: initial;
    .q-field__prepend {
      path {
        fill: #78839c;
      }
    }

    input {
      color: #78839c;
    }
  }
}
#q-datetime-modal {
  .radius {
    border-radius: 20px;
  }
  .q-time,
  .q-date {
    height: 430px;
    box-shadow: none;
    background: var(--vdatetime-popup-background);
    border-radius: 0;
    color: var(--vdatetime-popup-color);
    .q-date__header,
    .q-time__header {
      background-color: var(--vdatetime-popup-day-background-selected);
    }
    .q-time__clock-pointer {
      color: var(--vdatetime-popup-day-background-selected);
    }
    .q-time__clock-position--active {
      background-color: var(--vdatetime-popup-day-background-selected);
    }
    .q-date__content {
      .q-btn {
        padding-left: 5px !important;
        padding-right: 5px !important;
      }
    }
    .q-date__calendar-item {
      .bg-primary {
        background-color: var(--vdatetime-popup-day-background-selected) !important;
      }
    }
    .dialog-controls {
      padding-left: 15px;
      padding-right: 15px;
      color: var(--vdatetime-popup-day-background-selected);
      &:nth-child(1) {
        color: var(--delete-button-color);
      }
    }
  }
  .q-date {
    width: 300px;
    .bg-primary {
      background-color: var(--vdatetime-popup-day-background-selected) !important;
    }
  }
  .q-time {
    .q-time__now-button {
      background-color: var(--vdatetime-popup-day-background-selected);
    }
  }
}
</style>

<style scoped lang="scss">
.valid-error-message {
  position: static;
}

.form-field-label-text {
  font-size: 14px;
  font-weight: 600;
  line-height: 27px;
  word-wrap: break-word;
  padding: 1px;
}
</style>
